/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { useLocalization } from '@/data/Localization';
import { StoreDetails } from '@/data/types/Store';
import { findStoreAttributeByNameStartsWith, getStoreURL } from '@/data/utils/StoreStock';
import { Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { storeCityStyles } from '../../StoreLocatorCity/styles/storeCityStyles';
import { streetPageStoreBejijkButtonSX, streetPageStoreClosedColorSX } from '../styles/storeLocatorLeaf';

export const StoreLocatorCard: FC<{
	storeDetails: StoreDetails;
}> = ({ storeDetails }) => {
	const storeDetailsNLS = useLocalization('StoreLocator');
	const storeLocatorRoute = useLocalization('Routes').StoreLocator.route.t();
	let hasButton = true;
	let storeTodayOpen = true;
	const buttonUrl = getStoreURL(storeDetails, storeLocatorRoute);
	if (buttonUrl === storeLocatorRoute) {
		hasButton = false;
	}
	// Store Open Close logic per card . 
	const specialTimes = findStoreAttributeByNameStartsWith(storeDetails?.attributes, 'specialopen');

	const today = new Date();
	const todayDay = today?.toLocaleDateString("nl-NL", { weekday: 'long' });
	const formatter = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
	const formattedDate = formatter?.format(today);
	const specialHoursToday = specialTimes?.find((spDate) => spDate.displayName?.includes(formattedDate));
	const todayTimes = storeDetails?.attributes?.find((attr) => (attr.displayName?.toLowerCase() === todayDay?.toLowerCase())  && attr.name.startsWith('open'));
	let storeClosedToday = ((specialHoursToday?.displayValue?.toLowerCase() === 'gesloten') &&
		(todayTimes?.displayValue?.toLowerCase() === 'gesloten')) || (specialHoursToday?.displayValue === undefined && (todayTimes?.displayValue?.toLowerCase() === 'gesloten'));
	let StoreNowOpen = false;
	if (storeClosedToday) {
		storeTodayOpen = false;
	}
	else {
		let storeTimeRange: string[] = [];
		if (todayTimes?.displayValue?.toLowerCase() !== 'gesloten') {
			storeTimeRange = todayTimes?.displayValue?.split(' - ') || [];
		} else {
			storeTimeRange = specialHoursToday?.displayValue?.split(' - ') || [];
		}
		const storeOpenTime = storeTimeRange?.[0]?.split(':');
		const storeCloseTime = storeTimeRange?.[1]?.split(':');
		let storeOpenDateTimeInCET = new Date();
		let storeCloseDateTimeInCET = new Date();
		const nowInCET = new Date();

		const getUpdatedTime = (hours: number, minutes: number): Date => {
			const updatedTime = new Date();
			updatedTime.setHours(hours);
			updatedTime.setMinutes(minutes);
			return updatedTime;
		};

		if (storeOpenTime && storeOpenTime.length === 2) {
			const [openHour, openMinute] = storeOpenTime.map(Number);
			storeOpenDateTimeInCET = getUpdatedTime(openHour, openMinute);
		}
		if (storeCloseTime && storeCloseTime.length === 2) {
			const [closeHour, closeMinute] = storeCloseTime.map(Number);
			storeCloseDateTimeInCET = getUpdatedTime(closeHour, closeMinute);
		}
		StoreNowOpen = storeOpenDateTimeInCET <= nowInCET && nowInCET <= storeCloseDateTimeInCET;
	}

	return (
		<Card
			sx={storeCityStyles}
			id={storeDetails.storeName}
			data-testid={storeDetails.storeName}
		>
			<CardContent>
				<Stack spacing={1}>
					<Typography
						variant="h4"
						align="center"
						id={`${storeDetails.storeFullAddress}-name`}
						data-testid={`${storeDetails.storeFullAddress}-name`}
					>		{storeDetails.storeName}
					</Typography>
					<Typography
						variant="body2AsH2"
						align="center"
						sx={{ marginTop: '15px' }}
						id={`${storeDetails.storeFullAddress}-name`}
						data-testid={`${storeDetails.storeFullAddress}-name`}
					>{storeDetails.storeFullAddress}
					</Typography>
					<Typography
						variant="body2AsH2"
						align="center"
						id={`${storeDetails.displayAddress}-name`}
						data-testid={`${storeDetails.displayAddress}-name`}
					>{storeDetails.postalCode}{storeDetails.city}
					</Typography>
					<Typography
						variant="body2AsH2"
						align="center"
						id={`${storeDetails.displayAddress}-name`}
						data-testid={`${storeDetails.displayAddress}-name`}
					>{storeDetails.phone}
					</Typography>
					{storeTodayOpen && StoreNowOpen ?
						<Typography align="center" sx={{ color: '#368632;' }}>{storeDetailsNLS.storeStreetOpenToday.t()} </Typography>
						:
						<Typography align="center" sx={streetPageStoreClosedColorSX}>{storeDetailsNLS.storeStreetClosedToday.t()} </Typography>
					}
					{hasButton && <Linkable href={buttonUrl} sx={streetPageStoreBejijkButtonSX}><Button variant='semiConversion'>{storeDetailsNLS.storeStreetGoToStore.t()}</Button></Linkable>}
				</Stack>
			</CardContent>
		</Card>
	);
};
